<template>
    <div
        class="placeholder d-flex"
        :class="rootClasses"
        :style="{ minHeight: `${effectiveMinHeight}px` }"
    >
        <div class="placeholder__inner d-flex align-center" :class="innerClasses">
            <div class="placeholder__content" :class="contentClasses">
                <div
                    v-if="!isBanner"
                    class="placeholder__icon-wrap d-flex justify-center w-100"
                >
                    <slot name="illustration"><MasterSvg
                        :use="icon"
                        :width="iconWidth"
                        class="placeholder__icon flex-shrink-0"
                    /></slot>
                </div>
                <MasterSvg
                    v-else
                    :use="icon"
                    :width="iconWidth"
                    class="placeholder__icon flex-shrink-0"
                />
                <div class="placeholder__text" :class="{ 'flex-grow-1': isBanner }">
                    <div v-if="title" class="placeholder--title">
                        {{ title }}
                    </div>
                    <div
                        v-if="description || actions"
                        class="placeholder__meta d-flex text-caption text-gs-semibold"
                        :class="metaClasses"
                    >
                        <span
                            v-if="description"
                            class="placeholder--description"
                            v-html="description"
                        />
                        <div
                            v-if="actions?.length"
                            class="placeholder--actions d-flex"
                            :class="actionsClasses"
                        >
                            <v-btn
                                v-for="action in displayActions"
                                :key="action.action"
                                :disabled="action.disabled"
                                :class="[
                                    {
                                        primary: action.main,
                                        'mt-0 ml-3': isBanner,
                                        'mt-4': !isBanner,
                                    },
                                    action.class,
                                ]"
                                :loading="action.loading"
                                :small="action.size === 'small'"
                                :x-small="action.size === 'x-small'"
                                :text="action.type == 'text' || !action.main"
                                @click="onAction(action)"
                            >
                                <v-icon :icon="action.icon ? iconName(action.icon) : null" />
                                <span>{{ action.text }}</span>
                            </v-btn>
                        </div>
                    </div>
                    <div class="d-flex" :class="{ 'justify-center': !isBanner }">
                        <slot />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import GlobalMixins from '@/mixins/global';

export default {
    mixins: [GlobalMixins],
    props: {
        icon: {
            type: String,
            default: 'folder',
        },
        width: {
            type: Number,
            default: null,
        },
        title: {
            type: String,
            required: false,
        },
        description: {
            type: String,
            required: false,
        },
        actions: {
            type: Array,
            required: false,
            default() {
                return [];
            },
        },
        noPadding: {
            type: Boolean,
            default: false,
        },
        minHeight: {
            type: Number,
            default: null,
        },
        /** `default` — centered; `banner` — wide, low, pastel blue strip */
        variant: {
            type: String,
            default: 'default',
            validator: (value) => ['default', 'banner'].includes(value),
        },
    },
    computed: {
        isBanner() {
            return this.variant === 'banner';
        },
        effectiveMinHeight() {
            if (this.minHeight != null) {
                return this.minHeight;
            }
            return this.isBanner ? 100 : 300;
        },
        iconWidth() {
            if (this.width != null) {
                return this.width;
            }
            return this.isBanner ? 56 : 120;
        },
        rootClasses() {
            if (this.isBanner) {
                return [
                    'placeholder--banner',
                    'primary-lighter3',
                    'justify-center',
                    'align-center',
                    'rounded',
                    { 'pa-4': !this.noPadding },
                ];
            }
            return [
                'justify-center',
                { 'pa-4 pa-sm-6 py-sm-8': !this.noPadding },
            ];
        },
        innerClasses() {
            return {
                'justify-center flex-grow-1 w-100': this.isBanner,
                'justify-center w-100': !this.isBanner,
            };
        },
        contentClasses() {
            return this.isBanner
                ? 'd-flex flex-row align-center text-left flex-grow-1'
                : 'd-flex flex-column align-center text-center';
        },
        metaClasses() {
            return this.isBanner
                ? 'flex-column flex-sm-row align-sm-center mt-2'
                : 'flex-column align-center mt-3 px-md-6';
        },
        actionsClasses() {
            return this.isBanner ? 'flex-row flex-wrap align-center' : 'flex-column';
        },
        displayActions() {
            const actions = this.actions || [];
            const singleAction = actions.length === 1;
            return actions.map((action) => ({
                ...action,
                main: action.main ?? singleAction,
            }));
        },
    },
    methods: {
        onAction(action) {
            this.$emit(action.action);
            this.$emit('on-action', action.action);
        },
    },
};
</script>

<style lang="scss">
.placeholder {
    max-width: 100%;
}

.placeholder__inner,
.placeholder__content,
.placeholder__text {
    min-width: 0;
    max-width: 100%;
}

.placeholder--banner {
    width: 100%;
}

.placeholder--banner .placeholder__icon {
    margin-right: 20px;
    width: 56px;
    min-width: 56px;
    flex-shrink: 0;

    > div,
    svg {
        width: 100%;
        height: auto;
        display: block;
    }
}

.placeholder--title {
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 17px;
    margin-top: 16px;
    overflow-wrap: anywhere;
}

.placeholder--banner .placeholder--title {
    margin-top: 0;
    font-size: 16px;
    line-height: 1.35;
}

.placeholder--actions {
    display: block;
    max-width: 100%;
}

.placeholder--actions .v-btn {
    font-size: 12px;
    letter-spacing: normal;
    text-transform: none;
    height: auto !important;
    min-height: 32px;
    padding: 6px 12px;
    max-width: 100%;
}

.placeholder--description {
    max-width: 900px;
    font-size: 13px;
    line-height: 1.5;
    overflow-wrap: anywhere;
}

.placeholder--banner .placeholder--description {
    line-height: 1.45;
    max-width: none;
}

.placeholder__content.d-flex.flex-column .placeholder__text {
    width: 100%;
}

.placeholder__icon-wrap .placeholder__icon > div {
    margin-left: auto;
    margin-right: auto;
}

.placeholder__icon-wrap .placeholder__icon svg {
    display: block;
    width: 100%;
    height: auto;
}

// xs: long action labels must wrap inside the card instead of being clipped
// on both sides, and the illustration must leave room for the copy.
@media (max-width: 599.98px) {
    .placeholder--actions .v-btn {
        height: auto;
        min-height: 36px;
        padding-top: 6px;
        padding-bottom: 6px;
    }

    .placeholder--actions .v-btn .v-btn__content {
        max-width: 100%;
        white-space: normal;
    }

    .placeholder:not(.placeholder--banner) .placeholder__icon {
        max-width: 160px;
    }
}
</style>
